export type SharePickerPerson = {
  id: string
  firstName: string
  lastName: string
  email: string
  type: 'contact' | 'user'
}

export function normalizeRecipientEmail(email: string | undefined | null): string | null {
  const normalized = email?.trim().toLowerCase()
  return normalized || null
}

export function isShareRecipientAlreadySelected<T extends { id: string; email: string }>(
  item: T,
  selected: T[]
): boolean {
  if (selected.some((s) => s.id === item.id)) return true
  const email = normalizeRecipientEmail(item.email)
  if (!email) return false
  return selected.some((s) => normalizeRecipientEmail(s.email) === email)
}

export function mergeUniqueShareRecipients<T extends { id: string; email: string }>(
  prev: T[],
  additions: T[]
): T[] {
  const next = [...prev]
  for (const item of additions) {
    if (!isShareRecipientAlreadySelected(item, next)) {
      next.push(item)
    }
  }
  return next
}

/** Account CRM contacts in the loaded directory that are not already in the selection. */
export function remainingAccountContacts<T extends SharePickerPerson>(
  directory: T[],
  selected: T[]
): T[] {
  return directory.filter(
    (item) => item.type === 'contact' && !isShareRecipientAlreadySelected(item, selected)
  )
}

function matchesInviteSearch<T extends SharePickerPerson>(item: T, query: string): boolean {
  const q = query.toLowerCase()
  return (
    item.firstName.toLowerCase().includes(q) ||
    item.lastName.toLowerCase().includes(q) ||
    item.email.toLowerCase().includes(q)
  )
}

/**
 * Rows for the room-share typeahead. Does not silently cap the list — the dropdown
 * scrolls, and the API contact take is the performance ceiling.
 */
export function filterInvitePickerItems<T extends SharePickerPerson>(args: {
  pool: T[]
  selected: T[]
  searchTerm: string
  isCommunityFilterPending?: boolean
}): T[] {
  if (args.isCommunityFilterPending) return []

  const available = args.pool.filter(
    (item) => !isShareRecipientAlreadySelected(item, args.selected)
  )
  const trimmed = args.searchTerm.trim()
  if (trimmed.length === 0) return available
  return available.filter((item) => matchesInviteSearch(item, trimmed))
}
